import React, { useEffect, useState } from 'react';
import { X, Loader2 } from 'lucide-react';
import { employeeLeaveBalanceApi } from '../services/api';
import { LeaveBalanceBreakdown, LeaveBalanceBreakdownLine } from '../types';
import { formatCompanyDate } from '../utils/companyDateFormat';

type Props = {
  balanceId: number;
  onClose: () => void;
};

function formatQty(value: number, unit: 'hours' | 'days'): string {
  const abs = Math.abs(value);
  const rounded = abs.toFixed(2);
  const label = unit === 'hours' ? (abs === 1 ? 'hour' : 'hours') : abs === 1 ? 'day' : 'days';
  return `${value < 0 ? '−' : ''}${rounded} ${label}`;
}

function LineTable({
  lines,
  unit,
  showHours,
}: {
  lines: LeaveBalanceBreakdownLine[];
  unit: 'hours' | 'days';
  showHours?: boolean;
}) {
  if (lines.length === 0) {
    return <p className="text-sm text-neutral-500">None.</p>;
  }

  return (
    <div className="overflow-x-auto border border-neutral-200 rounded-md">
      <table className="min-w-full text-sm">
        <thead className="bg-neutral-50 text-left text-neutral-600">
          <tr>
            <th className="px-3 py-2 font-medium">When</th>
            <th className="px-3 py-2 font-medium">What</th>
            {showHours && <th className="px-3 py-2 font-medium text-right">Paid hours</th>}
            <th className="px-3 py-2 font-medium text-right">Amount</th>
          </tr>
        </thead>
        <tbody>
          {lines.map((line, index) => (
            <tr key={`${line.date ?? ''}-${line.label}-${index}`} className="border-t border-neutral-100">
              <td className="px-3 py-2 whitespace-nowrap text-neutral-700">
                {line.date ? formatCompanyDate(line.date) : '—'}
              </td>
              <td className="px-3 py-2 text-neutral-800">
                <div className="font-medium">{line.label}</div>
                {line.detail && <div className="text-xs text-neutral-500">{line.detail}</div>}
              </td>
              {showHours && (
                <td className="px-3 py-2 text-right text-neutral-700">
                  {line.hoursWorked != null ? line.hoursWorked.toFixed(2) : '—'}
                </td>
              )}
              <td className="px-3 py-2 text-right font-medium text-neutral-900 whitespace-nowrap">
                {formatQty(line.amount, unit)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const LeaveBalanceBreakdownModal: React.FC<Props> = ({ balanceId, onClose }) => {
  const [breakdown, setBreakdown] = useState<LeaveBalanceBreakdown | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setBreakdown(null);
    setError(null);
    employeeLeaveBalanceApi.getBreakdown(balanceId)
      .then((data) => {
        if (!cancelled) setBreakdown(data);
      })
      .catch(() => {
        if (!cancelled) setError('The balance calculation could not be loaded.');
      });
    return () => {
      cancelled = true;
    };
  }, [balanceId]);

  const unit = breakdown?.displayUnit ?? 'days';
  const showHours = !!breakdown?.accruals.some((line) => line.hoursWorked != null);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto p-4">
      <div className="bg-white rounded-lg w-full max-w-3xl my-8 max-h-[85vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-neutral-200 flex items-start justify-between gap-4">
          <div>
            <h3 className="text-lg font-semibold text-neutral-900">
              {breakdown ? `How ${breakdown.leaveTypeName} is calculated` : 'How this balance is calculated'}
            </h3>
            {breakdown && (
              <p className="text-sm text-neutral-500 mt-1">Leave year {breakdown.leaveYear}</p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-neutral-500 hover:text-neutral-800"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="px-6 py-4 space-y-6">
          {!breakdown && !error && (
            <div className="flex justify-center py-10">
              <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
            </div>
          )}

          {error && <p className="text-sm text-red-600">{error}</p>}

          {breakdown && (
            <>
              <p className="text-sm text-neutral-700">{breakdown.methodSummary}</p>

              <section>
                <h4 className="text-sm font-semibold text-neutral-800 mb-2">Accrued</h4>
                <LineTable lines={breakdown.accruals} unit={unit} showHours={showHours} />
              </section>

              <section>
                <h4 className="text-sm font-semibold text-neutral-800 mb-2">Deducted</h4>
                <LineTable lines={breakdown.deductions} unit={unit} />
              </section>

              {breakdown.pending.length > 0 && (
                <section>
                  <h4 className="text-sm font-semibold text-neutral-800 mb-2">Pending</h4>
                  <p className="text-xs text-neutral-500 mb-2">
                    These days are held against the balance and are not yet taken.
                  </p>
                  <LineTable lines={breakdown.pending} unit={unit} />
                </section>
              )}

              {breakdown.excluded.length > 0 && (
                <section>
                  <h4 className="text-sm font-semibold text-neutral-800 mb-2">Not deducted</h4>
                  <LineTable lines={breakdown.excluded} unit={unit} />
                </section>
              )}

              <section className="bg-neutral-50 border border-neutral-200 rounded-md p-4">
                <h4 className="text-sm font-semibold text-neutral-800 mb-3">Current balance</h4>
                <dl className="space-y-1 text-sm">
                  <div className="flex justify-between gap-4">
                    <dt className="text-neutral-600">{breakdown.startingLabel}</dt>
                    <dd className="font-medium">{formatQty(breakdown.starting, unit)}</dd>
                  </div>
                  <div className="flex justify-between gap-4">
                    <dt className="text-neutral-600">Carried forward</dt>
                    <dd className="font-medium">{formatQty(breakdown.carriedForward, unit)}</dd>
                  </div>
                  <div className="flex justify-between gap-4">
                    <dt className="text-neutral-600">Taken</dt>
                    <dd className="font-medium">−{formatQty(breakdown.taken, unit).replace(/^−/, '')}</dd>
                  </div>
                  <div className="flex justify-between gap-4">
                    <dt className="text-neutral-600">Pending</dt>
                    <dd className="font-medium">−{formatQty(breakdown.pendingAmount, unit).replace(/^−/, '')}</dd>
                  </div>
                  <div className="flex justify-between gap-4 border-t border-neutral-200 pt-2 mt-2">
                    <dt className="font-semibold text-neutral-900">Remaining</dt>
                    <dd className="font-semibold text-blue-700">{formatQty(breakdown.remaining, unit)}</dd>
                  </div>
                </dl>
                <p className="text-xs text-neutral-500 mt-3">
                  Remaining = {breakdown.startingLabel} + Carried forward − Taken − Pending.
                  {breakdown.totalsMatch
                    ? ' These figures match the current balance.'
                    : ` These figures add up to ${formatQty(breakdown.summedRemaining, unit)}. The balance currently shows ${formatQty(breakdown.remaining, unit)}.`}
                </p>
              </section>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default LeaveBalanceBreakdownModal;
